import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './accountItem.module.scss';
import clx from 'classnames';
import HoveredInfoIcon from 'Containers/Common/HoveredInfoIcon';
import { useDispatch } from 'react-redux';
import useWindowSize from 'utils/useWindowSize';
import { getSubSections, useSections } from '../constants';
import { authActions } from 'Containers/Auth/store';
import { toastActions } from 'Containers/Toasts/store';
import { ChevronDown, KeyRound, Mail, Pencil, Trash2 } from 'lucide-react';

const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

export const AccountItem = ({ account, openEditAccountModal, setAccountToDelete }) => {
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const { t } = useTranslation();
  const { SECTIONS } = useSections();
  const chairName = account?.nome_referente || '—';
  const permissions = Array.isArray(account?.permessi) ? account.permessi : [];

  const sendRecoveryEmail = () => {
    dispatch(
      authActions.SEND_EMAIL_RECOVERY.REQUEST({ email: account.email }, () => {
        dispatch(
          toastActions.OPEN_TOAST.SUCCESS({
            type: 'api',
            props: {
              title: t('settings.accountItem.recoverPassword.toast.title'),
              message: t('settings.accountItem.recoverPassword.toast.message'),
              warning: false,
            },
          }),
        );
      }),
    );
  };

  return (
    <details
      className={clx(styles.account_card, {
        [styles.account_card_mbl]: isMobile,
      })}
    >
      <summary className={styles.summary}>
        <div className={styles.avatar} aria-hidden>
          {getInitials(account.nome_referente)}
        </div>
        <div className={styles.summary_copy}>
          <div className={styles.summary_name}>{account.nome_referente}</div>
          <div className={styles.summary_meta}>
            <span>{account.email}</span>
          </div>
        </div>
        <span className={styles.perm_count}>
          {t('settings.accountItem.permissionsCount', {
            count: permissions.filter((p) => SECTIONS[p.sezione]).length,
          })}
        </span>
        <span className={styles.chevron} aria-hidden>
          <ChevronDown size={18} strokeWidth={2.4} />
        </span>
      </summary>

      <div className={styles.body}>
        <div className={styles.info_grid}>
          <div className={styles.info_block}>
            <div className={styles.info_label}>
              <span>{t('settings.accountItem.chair')}</span>
              <button
                type="button"
                className={styles.icon_btn}
                onClick={() => openEditAccountModal(account)}
                aria-label={t('common.actions.edit')}
              >
                <Pencil size={14} strokeWidth={2.3} />
              </button>
            </div>
            <div className={styles.info_value}>{chairName}</div>
          </div>
          <div className={styles.info_block}>
            <div className={styles.info_label}>
              <span>{t('settings.accountItem.email')}</span>
              <button
                type="button"
                className={styles.icon_btn}
                onClick={() => openEditAccountModal(account)}
                aria-label={t('common.actions.edit')}
              >
                <Pencil size={14} strokeWidth={2.3} />
              </button>
            </div>
            <div className={styles.info_value}>{account.email}</div>
          </div>
        </div>

        <div className={styles.actions_row}>
          <div className={styles.action_block}>
            <div className={styles.action_title}>
              <span>{t('settings.accountItem.recoverPassword.title')}</span>
              <HoveredInfoIcon
                description={t('settings.accountItem.recoverPassword.description')}
              />
            </div>
            <button type="button" className={styles.primary_btn} onClick={sendRecoveryEmail}>
              <Mail size={16} strokeWidth={2.2} />
              {t('settings.accountItem.recoverPassword.button')}
            </button>
          </div>
          <div className={styles.action_block}>
            <div className={styles.action_title}>
              <span>{t('settings.accountItem.deactivateAccount.title')}</span>
              <HoveredInfoIcon
                description={t('settings.accountItem.deactivateAccount.description')}
              />
            </div>
            <button
              type="button"
              className={styles.danger_btn}
              onClick={() => setAccountToDelete(account)}
            >
              <Trash2 size={16} strokeWidth={2.2} />
              {t('settings.accountItem.deactivateAccount.button')}
            </button>
          </div>
        </div>

        <div className={styles.permissions}>
          <div className={styles.info_label}>
            <span>{t('settings.accountItem.permissions')}</span>
            <button
              type="button"
              className={styles.icon_btn}
              onClick={() => openEditAccountModal(account)}
              aria-label={t('common.actions.edit')}
            >
              <Pencil size={14} strokeWidth={2.3} />
            </button>
          </div>
          <div className={styles.permissions_list}>
            {permissions.map((permesso, index) => {
              const section = SECTIONS[permesso.sezione];
              if (!section) return null;
              const subSections = getSubSections(permesso);
              return (
                <div key={`${account.idUtenteBusiness}-${permesso.sezione}-${index}`} className={styles.permission_chip}>
                  <span className={styles.permission_section}>
                    <KeyRound size={12} strokeWidth={2.3} aria-hidden />
                    {section}
                  </span>
                  {subSections.length ? (
                    <span className={styles.permission_subs}>
                      {subSections.map((subSection, subIndex) => (
                        <Fragment key={`${subSection}-${subIndex}`}>
                          {subIndex > 0 ? <span className={styles.sub_divider}>·</span> : null}
                          <span>{subSection}</span>
                        </Fragment>
                      ))}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </details>
  );
};
